import { defineStore } from "pinia";
import { fetchKioskBranding, KioskRequestError } from "@/kiosk/services/kioskApi.service";
import type { KioskPublicBrandingResponse, KioskResolvedBranding } from "@/kiosk/types/kiosk";
import { resolveKioskBranding } from "@/kiosk/utils/kioskBranding.utils";

type KioskBrandingLoadState = "idle" | "loading" | "ready" | "fallback" | "unavailable";

const UNAVAILABLE_STATUSES = new Set([401, 403]);
const KIOSK_BRANDING_CACHE_PREFIX = "kiosk-branding:";
const KIOSK_BRANDING_CACHE_TTL_MS = 60 * 60 * 1000;

const getCacheKey = (tokenHash: string): string => `${KIOSK_BRANDING_CACHE_PREFIX}${tokenHash}`;

const readCachedBranding = (tokenHash: string): KioskPublicBrandingResponse | null => {
  if (typeof localStorage === "undefined" || !tokenHash) {
    return null;
  }

  const rawValue = localStorage.getItem(getCacheKey(tokenHash));
  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as {
      cachedAt?: number;
      branding?: KioskPublicBrandingResponse | null;
    };
    const cachedAt = Number(parsedValue?.cachedAt ?? 0);
    const isFresh =
      Number.isFinite(cachedAt) && Date.now() - cachedAt < KIOSK_BRANDING_CACHE_TTL_MS;

    if (!isFresh || !parsedValue?.branding) {
      localStorage.removeItem(getCacheKey(tokenHash));
      return null;
    }

    return parsedValue.branding;
  } catch {
    localStorage.removeItem(getCacheKey(tokenHash));
    return null;
  }
};

const writeCachedBranding = (tokenHash: string, branding: KioskPublicBrandingResponse): void => {
  if (typeof localStorage === "undefined" || !tokenHash) {
    return;
  }

  localStorage.setItem(
    getCacheKey(tokenHash),
    JSON.stringify({
      cachedAt: Date.now(),
      branding,
    }),
  );
};

const clearCachedBranding = (tokenHash: string): void => {
  if (typeof localStorage === "undefined" || !tokenHash) {
    return;
  }

  localStorage.removeItem(getCacheKey(tokenHash));
};

export const useKioskBrandingStore = defineStore("kioskBranding", {
  state: () => ({
    tokenHash: null as string | null,
    rawBranding: null as KioskPublicBrandingResponse | null,
    loadState: "idle" as KioskBrandingLoadState,
    unavailableStatus: null as number | null,
    lastLoadErrorStatus: null as number | null,
    hasHydratedBranding: false,
  }),
  getters: {
    resolvedBranding(state): KioskResolvedBranding {
      return resolveKioskBranding(state.rawBranding);
    },
    isLoading: (state) => state.loadState === "loading",
    isUnavailable: (state) => state.loadState === "unavailable",
    isBootstrapping(state): boolean {
      return state.loadState === "loading" && !state.hasHydratedBranding;
    },
    businessName(): string {
      return this.resolvedBranding.businessName;
    },
    displayTitle(): string {
      return this.resolvedBranding.displayTitle;
    },
    displaySubtitle(): string {
      return this.resolvedBranding.displaySubtitle;
    },
    logoUrl(): string {
      return this.resolvedBranding.logoUrl;
    },
    iconUrl(): string {
      return this.resolvedBranding.iconUrl;
    },
    primaryColor(): string {
      return this.resolvedBranding.primaryColor;
    },
    primaryColorLight(): string {
      return this.resolvedBranding.primaryColorLight;
    },
    secondaryColor(): string {
      return this.resolvedBranding.secondaryColor;
    },
  },
  actions: {
    reset(tokenHash: string | null = null): void {
      this.tokenHash = tokenHash;
      this.rawBranding = null;
      this.loadState = "idle";
      this.unavailableStatus = null;
      this.lastLoadErrorStatus = null;
      this.hasHydratedBranding = false;
    },
    hydrateCachedBranding(tokenHash: string): boolean {
      if (!tokenHash) {
        this.reset(null);
        return false;
      }

      const cachedBranding = readCachedBranding(tokenHash);
      this.tokenHash = tokenHash;

      if (!cachedBranding) {
        this.rawBranding = null;
        this.loadState = "idle";
        this.unavailableStatus = null;
        this.lastLoadErrorStatus = null;
        this.hasHydratedBranding = false;
        return false;
      }

      this.rawBranding = cachedBranding;
      this.loadState = "ready";
      this.unavailableStatus = null;
      this.lastLoadErrorStatus = null;
      this.hasHydratedBranding = true;
      return true;
    },
    async loadBranding(
      tokenHash: string,
      options: { force?: boolean } = {},
    ): Promise<KioskResolvedBranding> {
      if (!tokenHash) {
        this.reset(null);
        return this.resolvedBranding;
      }

      const hasLoadedTokenHash =
        this.tokenHash === tokenHash && this.loadState !== "idle" && this.loadState !== "loading";

      if (hasLoadedTokenHash && !options.force) {
        return this.resolvedBranding;
      }

      const isSameTokenHash = this.tokenHash === tokenHash;
      const hasExistingBranding = isSameTokenHash && !!this.rawBranding;

      this.tokenHash = tokenHash;
      this.loadState = "loading";
      this.unavailableStatus = null;
      this.lastLoadErrorStatus = null;

      if (!isSameTokenHash) {
        this.rawBranding = null;
        this.hasHydratedBranding = false;
      } else {
        this.hasHydratedBranding = hasExistingBranding || this.hasHydratedBranding;
      }

      try {
        const branding = await fetchKioskBranding(tokenHash);
        if (this.tokenHash !== tokenHash) {
          return this.resolvedBranding;
        }

        this.rawBranding = branding;
        this.loadState = "ready";
        this.unavailableStatus = null;
        this.lastLoadErrorStatus = null;
        this.hasHydratedBranding = true;
        writeCachedBranding(tokenHash, branding);
      } catch (error) {
        if (this.tokenHash !== tokenHash) {
          return this.resolvedBranding;
        }

        if (error instanceof KioskRequestError) {
          this.lastLoadErrorStatus = error.status;

          if (UNAVAILABLE_STATUSES.has(error.status)) {
            clearCachedBranding(tokenHash);
            this.rawBranding = null;
            this.loadState = "unavailable";
            this.unavailableStatus = error.status;
            this.hasHydratedBranding = false;
          } else if (error.status === 404) {
            clearCachedBranding(tokenHash);

            if (this.rawBranding) {
              this.loadState = "ready";
              this.unavailableStatus = null;
              this.hasHydratedBranding = true;
            } else {
              this.rawBranding = null;
              this.loadState = "fallback";
              this.unavailableStatus = null;
              this.hasHydratedBranding = true;
            }
          } else if (this.rawBranding) {
            this.loadState = "ready";
            this.unavailableStatus = null;
            this.hasHydratedBranding = true;
          } else {
            this.rawBranding = null;
            this.loadState = "fallback";
            this.unavailableStatus = null;
            this.hasHydratedBranding = true;
          }
        } else if (this.rawBranding) {
          this.loadState = "ready";
          this.unavailableStatus = null;
          this.hasHydratedBranding = true;
        } else {
          this.rawBranding = null;
          this.loadState = "fallback";
          this.unavailableStatus = null;
          this.hasHydratedBranding = true;
        }
      }

      return this.resolvedBranding;
    },
  },
});
